import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ErrorText, SelectInput, TextArea } from './content-fields'
import type { Translate, VersionRowView } from './content-types'
import { StatusChipView } from './status-chip.view'

/**
 * Props for {@link VersionsPanelView}.
 *
 * @property selected - The version number shown in the language tabs.
 * @property newVersion - The "New version from vN" dialog, when open.
 * @property compare - The compare picker: a language and two version numbers.
 * @property onNewVersion - Present only with `content.write`.
 */
export interface VersionsPanelViewProps {
	t: Translate
	versions: VersionRowView[]
	selected: number
	newVersion: { basedOn: number; summary: string; error?: string; formError: string | null } | null
	compare: { locale: string; locales: string[]; from: string; to: string; options: number[] } | null
	isBusy: boolean
	onSelect: (versionNumber: number) => void
	onNewVersion?: (basedOn: number) => void
	onSummaryChange: (summary: string) => void
	onConfirmNewVersion: () => void
	onCancelNewVersion: () => void
	onOpenCompare: () => void
	onCompareChange: (field: 'locale' | 'from' | 'to', value: string) => void
	onRunCompare: () => void
	onCloseCompare: () => void
}

const windowText = (t: Translate, from: string | null, to: string | null): string | null =>
	from || to ? `${from ?? '…'} – ${to ?? t('dates.openEnded')}` : null

function CompareForm({ props }: { props: VersionsPanelViewProps }): ReactElement | null {
	const { t, compare } = props
	if (!compare) return null
	const options = compare.options.map((n) => ({ id: String(n), label: `v${n}` }))
	return (
		<fieldset className="flex flex-wrap items-end gap-3" aria-label={t('versions.compare')}>
			<SelectInput
				label={t('versions.compareLanguage')}
				value={compare.locale}
				options={compare.locales.map((l) => ({ id: l, label: l }))}
				onChange={(v) => props.onCompareChange('locale', v)}
			/>
			<SelectInput
				label={t('versions.compareFrom')}
				value={compare.from}
				options={options}
				onChange={(v) => props.onCompareChange('from', v)}
			/>
			<SelectInput
				label={t('versions.compareTo')}
				value={compare.to}
				options={options}
				onChange={(v) => props.onCompareChange('to', v)}
			/>
			<Button isDisabled={!compare.from || !compare.to || compare.from === compare.to} onPress={props.onRunCompare}>
				{t('versions.showDiff')}
			</Button>
			<Button variant="muted" onPress={props.onCloseCompare}>
				{t('actions.close')}
			</Button>
		</fieldset>
	)
}

function NewVersionDialog({ props }: { props: VersionsPanelViewProps }): ReactElement {
	const { t } = props
	return (
		<Dialog
			isOpen={props.newVersion !== null}
			onOpenChange={(open) => (open ? undefined : props.onCancelNewVersion())}
			title={t('versions.newFrom', { version: props.newVersion?.basedOn ?? 0 })}
			labels={{ close: t('actions.close') }}
		>
			<div className="flex flex-col gap-3">
				<ErrorText message={props.newVersion?.formError} />
				<TextArea
					label={t('versions.changeSummary')}
					value={props.newVersion?.summary ?? ''}
					error={props.newVersion?.error}
					onChange={props.onSummaryChange}
				/>
				<div className="flex gap-2">
					<Button isDisabled={props.isBusy} onPress={props.onConfirmNewVersion}>
						{t('versions.create')}
					</Button>
					<Button variant="muted" onPress={props.onCancelNewVersion}>
						{t('actions.cancel')}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}

function VersionList({ props }: { props: VersionsPanelViewProps }): ReactElement {
	const { t } = props
	return (
		<ul className="flex flex-col gap-2">
			{props.versions.map((v) => (
				<li
					key={v.id}
					aria-current={v.versionNumber === props.selected ? 'true' : undefined}
					className="flex flex-col gap-1 rounded-ds-md border border-ds-border p-3"
				>
					<div className="flex flex-wrap items-center gap-2">
						<Button variant="muted" size="sm" onPress={() => props.onSelect(v.versionNumber)}>
							{`v${v.versionNumber}`}
						</Button>
						<span className="text-ds-sm text-ds-text-muted">{v.createdAt}</span>
						{v.changeSummary ? <span className="text-ds-sm">{v.changeSummary}</span> : null}
						{props.onNewVersion ? (
							<Button variant="muted" size="sm" onPress={() => props.onNewVersion?.(v.versionNumber)}>
								{t('versions.newFrom', { version: v.versionNumber })}
							</Button>
						) : null}
					</div>
					<ul className="flex flex-wrap gap-2">
						{v.localizations.map((l) => (
							<li key={l.locale} className="flex items-center gap-1 text-ds-sm">
								<StatusChipView t={t} locale={l.locale} label={l.label} />
								{windowText(t, l.validFrom, l.validTo)}
							</li>
						))}
					</ul>
				</li>
			))}
		</ul>
	)
}

/** The versions of a text, newest first, with "New version from vN" and compare (organism). */
export function VersionsPanelView(props: VersionsPanelViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-3" aria-label={t('versions.title')}>
			<header className="flex items-center justify-between">
				<h2 className="text-ds-lg font-semibold text-ds-text">{t('versions.title')}</h2>
				<Button variant="muted" size="sm" onPress={props.onOpenCompare}>
					{t('versions.compare')}
				</Button>
			</header>
			<CompareForm props={props} />
			<VersionList props={props} />
			<NewVersionDialog props={props} />
		</section>
	)
}
